import { withoutBlankCategories } from '../../utils/categories';
import { blankHackathon as blank, copyOf, orgAccessPatch } from '../../utils/newHackathon';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { addJudgeToHackathon, createHackathon, getMyHackathons, HackathonPayload } from '../../services/hackathon.service';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import { EditHackathonData, HackathonEditor } from '../../components/Hackathon/HackathonEditor';
import { useMyOrganizations } from '../../hooks/useMyOrganizations';
import { useAuth } from '../../services/useAuth';
import Stack from '@mui/material/Stack/Stack';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import Typography from '@mui/material/Typography/Typography';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { login } from '../../services/auth.service';
import { clearDraft, loadDraft, saveDraft } from '../../services/hackathonDraft.service';
import { PageContainer } from '../../components/PageContainer';
import { HackathonPreview } from './HackathonPreview';
import { uploadLocalImages } from '../../services/draftImages';

export const NewHackathon = () => {
    // set up before signing in, it's kept in this browser: read once, on arrival
    const [draft] = useState(loadDraft);
    // the form is that draft until it's created or the person starts over
    const [continuingDraft, setContinuingDraft] = useState(!!draft);
    const [data, setData] = useState<EditHackathonData>(() => draft?.data || blank)
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState('');
    const { user, loading: authLoading } = useAuth();
    const signedOut = !authLoading && !user;
    const [previewing, setPreviewing] = useState(false);
    // the preview and the form are long: each starts at its top
    useEffect(() => { window.scrollTo(0, 0); }, [previewing]);
    // on the way to sign in: a late edit (the description saves after a pause) mustn't re-save the draft without its flag
    const leavingToSignIn = useRef(false);

    // default to the user's organization when there is exactly one
    const { organizations } = useMyOrganizations();
    const [organizationDefaulted, setOrganizationDefaulted] = useState(false);
    useEffect(() => {
        if (organizationDefaulted || organizations.length === 0) {
            return;
        }
        setOrganizationDefaulted(true);
        if (organizations.length === 1) {
            const org = organizations[0];
            // its access rule too: new hackathons in the org start with who can join
            setData(current => current.organization ? current : { ...current, organization: org._id, ...orgAccessPatch(org) });
        }
    }, [organizations, organizationDefaulted]);

    // the New hackathon dialog and "Run it again" land here as /new?from=<id or slug>; only then load the list
    const [params] = useSearchParams();
    const from = params.get('from');
    const [mine, setMine] = useState<HackathonPayload[] | null>(null);
    useEffect(() => {
        if (from && mine === null && user) {
            getMyHackathons().then(setMine, err => { console.error(err); setMine([]); });
        }
    }, [from, mine, user]);
    // duplicating is of your own past events, so it needs you signed in
    useEffect(() => {
        if (from && signedOut) {
            login();
        }
    }, [from, signedOut]);

    // keep the draft as it's edited (not while duplicating, which is always signed in and starts from the past event)
    const [draftNotKept, setDraftNotKept] = useState(false);
    useEffect(() => {
        if (!authLoading && !from && (!user || continuingDraft) && !leavingToSignIn.current) {
            setDraftNotKept(!saveDraft(data));
        }
    }, [data, authLoading, user, continuingDraft, from]);
    const organized = useMemo(() => (mine || [])
        .filter(h => !!user && h.organizers.some(o => o.email === user.email))
        .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)), [mine, user]);
    const [copiedFrom, setCopiedFrom] = useState<HackathonPayload | null>(null);
    // the form reads its rich-text fields once, so a copy (or undoing it) starts it afresh
    const [formKey, setFormKey] = useState(0);
    const copy = (past: HackathonPayload | null) => {
        setCopiedFrom(past);
        // back to scratch keeps the organization (and its access rule); a copy keeps its own, if still usable
        setData(current => past
            ? copyOf(past, organizations.map(o => o._id))
            : { ...blank, organization: current.organization, ...orgAccessPatch(organizations.find(o => o._id === current.organization)) });
        setFormKey(k => k + 1);
        setCopyJudges(true);
    };
    // the same judges, usually: they're re-added once the new event exists (Charles: "saved judges")
    const [copyJudges, setCopyJudges] = useState(true);

    // the form follows the address, also when the dialog is used again from this page
    const [appliedFrom, setAppliedFrom] = useState<string | null>(null);
    // ?from= that isn't one of your hackathons (deleted, or someone else's link): say so instead of doing nothing
    const fromMissing = !!from && mine !== null && !organized.some(h => h._id === from || h.slug === from);
    useEffect(() => {
        if (from === appliedFrom) {
            return;
        }
        if (!from) {
            setAppliedFrom(null);
            copy(null);
            return;
        }
        const past = organized.find(h => h._id === from || h.slug === from);
        if (past) {
            setAppliedFrom(from);
            copy(past);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, appliedFrom, organized]);

    const create = (current: EditHackathonData) => {
        setSubmitError('');

        // images added before signing in are only in the draft so far: upload them first
        return uploadLocalImages(current.branding).then(branding => createHackathon({
            ...withoutBlankCategories(current),
            branding,

            startDate: current.startDate?.toDate(),
            endDate: current.endDate?.toDate(),
            ideaSubmissionDeadline: current.ideaSubmissionDeadline?.toDate(),
        })).then(async res => {
            clearDraft();
            if (copiedFrom && copyJudges) {
                // one at a time; a judge who can't be added (account gone) shouldn't stop the event being created
                for (const judge of copiedFrom.judges) {
                    await addJudgeToHackathon(res._id, judge.email).catch(err => console.error(err));
                }
            }
            navigate(`/h/${res.slug}`);
        }).catch(err => {
            console.error(err);
            setSubmitError(err.response?.data?.message || 'Could not create the hackathon');
        });
    };

    // signed out, "create" means: keep it, sign in, and come back here to create it
    const submit = () => {
        if (signedOut) {
            if (!saveDraft(data, true)) {
                // signing in leaves the page: without the draft, all of this would be lost
                setDraftNotKept(true);
                return;
            }
            leavingToSignIn.current = true;
            login();
            return;
        }
        create(data);
    };

    // back from signing in after "Sign in to create": create it straight away, once
    const [creatingDraft, setCreatingDraft] = useState(false);
    const [draftCreateStarted, setDraftCreateStarted] = useState(false);
    useEffect(() => {
        if (user && draft?.createAfterSignIn && !draftCreateStarted) {
            setDraftCreateStarted(true);
            setCreatingDraft(true);
            // drop the flag first, so a failure or a reload shows the form instead of creating it twice
            saveDraft(draft.data);
            create(draft.data).finally(() => setCreatingDraft(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, draft, draftCreateStarted]);

    const startOver = () => {
        clearDraft();
        setContinuingDraft(false);
        copy(null);
    };

    const intro = (
        <Stack spacing={1}>
            {
                signedOut && draftNotKept && <Alert severity="warning">
                    This browser can't keep your hackathon (storage is full or blocked), so it would be lost when you sign in.
                    Try a smaller logo or banner, or sign in first and then set it up.
                </Alert>
            }
            {
                signedOut && <Alert severity="info" action={
                    <Button color="inherit" size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => setPreviewing(true)}>Preview</Button>
                }>
                    Sign in or create an account to share your hackathon with others, no credit card needed.
                </Alert>
            }
            {
                // signed in with a draft from before (not the one just created after signing in)
                !!user && continuingDraft && !draft?.createAfterSignIn && !from && <Alert severity="info"
                    action={<Button color="inherit" size="small" onClick={startOver}>Start from scratch instead</Button>}>
                    Here's the hackathon you started setting up earlier. Create it when you're ready.
                </Alert>
            }
            {fromMissing && <Alert severity="warning">That hackathon isn't one you organized, so it can't be duplicated here. You're starting from scratch.</Alert>}
            {
                copiedFrom && <Alert severity="success" role="status" action={<Button color="inherit" size="small" onClick={() => copy(null)}>Start from scratch instead</Button>}>
                    Duplicating <b>{copiedFrom.name}</b>: its categories, description, team sizes, access, scoring settings, pitch template and branding. Dates, ideas and participants start fresh.
                </Alert>
            }
            {
                copiedFrom && copiedFrom.judges.length > 0 && <FormControlLabel
                    control={<Checkbox checked={copyJudges} onChange={e => setCopyJudges(e.target.checked)} />}
                    label={`Also add the same judges: ${copiedFrom.judges.map(j => j.username || j.email).join(', ')}`} />
            }
        </Stack>
    );

    const submitLabel = signedOut ? 'Sign in to create' : 'Create Hackathon';

    if (creatingDraft) {
        return (
            <PageContainer>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 6 }} role="status">
                    <CircularProgress size={28} />
                    <Typography>Creating your hackathon…</Typography>
                </Stack>
            </PageContainer>
        );
    }

    if (previewing) {
        return (
            <HackathonPreview
                data={data}
                organizationBranding={organizations.find(o => o._id === data.organization)?.branding}
                onBack={() => setPreviewing(false)}
                action={<Button variant="contained" onClick={() => {
                    if (!data.name) {
                        // the form says why
                        setPreviewing(false);
                        return;
                    }
                    submit();
                }}>{submitLabel}</Button>} />
        );
    }

    return (
        <HackathonEditor
            key={formKey}
            headingLabel='Creating a new Hackathon'
            submitLabel={submitLabel}
            data={data}
            setData={setData}
            submitError={submitError}
            intro={intro}
            applyOrgAccess
            actions={<Button size="large" startIcon={<VisibilityOutlinedIcon />} onClick={() => setPreviewing(true)}>Preview</Button>}
            onSubmit={submit} />

    )
};
